import { createContext, useContext, useState, useEffect, useCallback } from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@/components/ThemeProvider";

// Pages
import HomePage from "./pages/HomePage";
import UniversePage from "./pages/UniversePage";
import CharacterPage from "./pages/CharacterPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import FavoritesPage from "./pages/FavoritesPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUniverses from "./pages/admin/AdminUniverses";
import AdminCharacters from "./pages/admin/AdminCharacters";
import AdminGallery from "./pages/admin/AdminGallery";
import AdminContent from "./pages/admin/AdminContent";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminMediaGallery from "./pages/admin/AdminMediaGallery";
import StorePage from "./pages/StorePage";
import LoreExplorerPage from "./pages/LoreExplorerPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ProductPage from "./pages/ProductPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrdersPage from "./pages/OrdersPage";
import WishlistPage from "./pages/WishlistPage";
import SharedConversationPage from "./pages/SharedConversationPage";
import TimelinePage from "./pages/TimelinePage";
import TheoriesPage from "./pages/TheoriesPage";
import CharacterChatPage from "./pages/CharacterChatPage";
import ComparePage from "./pages/ComparePage";
import SearchPage from "./pages/SearchPage";
import ProfilePage from "./pages/ProfilePage";
import QuizPage from "./pages/QuizPage";
import CommunityPage from "./pages/CommunityPage";
import SubmitContributionPage from "./pages/SubmitContributionPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import AdminContributions from "./pages/admin/AdminContributions";
import AdminNewsletter from "./pages/admin/AdminNewsletter";
import AdminFilmography from "./pages/admin/AdminFilmography";
import AdminUsers from "./pages/admin/AdminUsers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Add Authorization header from localStorage for cross-origin auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    if (response.data.access_token) {
      localStorage.setItem("access_token", response.data.access_token);
    }
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    if (response.data.access_token) {
      localStorage.setItem("access_token", response.data.access_token);
    }
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    localStorage.removeItem("access_token");
    setUser(false);
  };

  // Admin tab indicator - red favicon + "ADMIN" in title
  useEffect(() => {
    if (user && user.role === "admin") {
      document.title = "ADMIN | FanVerse";
      const canvas = document.createElement("canvas");
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#dc2626";
      ctx.beginPath();
      ctx.arc(16, 16, 16, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.font = "bold 12px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("A", 16, 17);
      const link = document.querySelector("link[rel*='icon']") || document.createElement("link");
      link.rel = "icon";
      link.href = canvas.toDataURL();
      document.head.appendChild(link);
      const meta = document.querySelector("meta[name='theme-color']");
      if (meta) meta.content = "#dc2626";
    } else {
      document.title = "FanVerse";
      const link = document.querySelector("link[rel*='icon']");
      if (link) link.href = "/favicon.svg";
      const meta = document.querySelector("meta[name='theme-color']");
      if (meta) meta.content = "#000000";
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030712]">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
      <HelmetProvider>
      <div className="App min-h-screen bg-[#030712]">
        <div className="noise-overlay"></div>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/universe/:slug" element={<UniversePage />} />
            <Route path="/character/:id" element={<CharacterPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/store" element={<StorePage />} />
            <Route path="/lore-explorer" element={<LoreExplorerPage />} />
            <Route path="/shared/:shareId" element={<SharedConversationPage />} />
            <Route path="/timeline/:slug" element={<TimelinePage />} />
            <Route path="/theories" element={<TheoriesPage />} />
            <Route path="/character-chat" element={<CharacterChatPage />} />
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/quiz" element={<QuizPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/store/product/:id" element={<ProductPage />} />
            
            {/* Protected Routes */}
            <Route path="/community/submit" element={
              <ProtectedRoute>
                <SubmitContributionPage />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/favorites" element={
              <ProtectedRoute>
                <FavoritesPage />
              </ProtectedRoute>
            } />
            <Route path="/cart" element={
              <ProtectedRoute>
                <CartPage />
              </ProtectedRoute>
            } />
            <Route path="/checkout" element={
              <ProtectedRoute>
                <CheckoutPage />
              </ProtectedRoute>
            } />
            <Route path="/orders" element={
              <ProtectedRoute>
                <OrdersPage />
              </ProtectedRoute>
            } />
            <Route path="/wishlist" element={
              <ProtectedRoute>
                <WishlistPage />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/universes" element={
              <ProtectedRoute adminOnly>
                <AdminUniverses />
              </ProtectedRoute>
            } />
            <Route path="/admin/characters" element={
              <ProtectedRoute adminOnly>
                <AdminCharacters />
              </ProtectedRoute>
            } />
            <Route path="/admin/gallery" element={
              <ProtectedRoute adminOnly>
                <AdminGallery />
              </ProtectedRoute>
            } />
            <Route path="/admin/media" element={
              <ProtectedRoute adminOnly>
                <AdminMediaGallery />
              </ProtectedRoute>
            } />
            <Route path="/admin/content" element={
              <ProtectedRoute adminOnly>
                <AdminContent />
              </ProtectedRoute>
            } />
            <Route path="/admin/products" element={
              <ProtectedRoute adminOnly>
                <AdminProducts />
              </ProtectedRoute>
            } />
            <Route path="/admin/orders" element={
              <ProtectedRoute adminOnly>
                <AdminOrders />
              </ProtectedRoute>
            } />
            <Route path="/admin/contributions" element={
              <ProtectedRoute adminOnly>
                <AdminContributions />
              </ProtectedRoute>
            } />
            <Route path="/admin/newsletter" element={
              <ProtectedRoute adminOnly>
                <AdminNewsletter />
              </ProtectedRoute>
            } />
            <Route path="/admin/filmography" element={
              <ProtectedRoute adminOnly>
                <AdminFilmography />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute adminOnly>
                <AdminUsers />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
        </div>
      </HelmetProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;

// Register service worker for PWA
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}
